import { apiClient } from './apiClient'
import { getAccessToken, getActiveTenantId } from './session'

export type BilanPeriod = 'day' | 'month' | 'year'
export type ExportFormat = 'pdf' | 'image' | 'excel'

export interface TopProductResponse {
  productName: string
  quantitySold: number
  percentageOfTotal: number
}

export interface DailyRevenueResponse {
  dayLabel: string
  revenue: number
  isToday: boolean
}

export interface DashboardResponse {
  salesToday: number
  salesYesterday: number
  /** null pour SELLER/SUPERVISOR — bénéfice réservé au propriétaire, cf. ReportingController.maskFinancials. */
  profitToday: number | null
  profitYesterday: number | null
  outstandingDebt: number
  debtorCount: number
  /** null pour SELLER/SUPERVISOR — valeur du stock réservée au propriétaire. */
  stockValue: number | null
  stockItemCount: number
  lowStockCount: number
  outOfStockCount: number
  /** null pour SELLER/SUPERVISOR — répartition brut/promotions/rabais/caisse réservée au propriétaire. */
  grossSalesToday: number | null
  promotionsToday: number | null
  discountsToday: number | null
  /** null si aucun comptage physique de caisse saisi aujourd'hui — distinct d'un écart de 0, cf. "Compter la caisse". */
  cashCollectedToday: number | null
  cashGapToday: number | null
  /** Espèces attendues aujourd'hui ; null pour SELLER/SUPERVISOR (réservé au propriétaire). */
  cashExpectedToday: number | null
  topProducts: TopProductResponse[]
  last7Days: DailyRevenueResponse[]
}

export interface DailyBreakdownResponse {
  date: string
  revenue: number
  margin: number
}

export interface TopProfitableProductResponse {
  productName: string
  quantitySold: number
  revenue: number
  margin: number
  marginPercent: number
}

export interface BilanResponse {
  periodStart: string
  periodEnd: string
  revenue: number
  costOfGoodsSold: number
  profit: number
  outstandingDebt: number
  grossSales: number
  promotions: number
  discounts: number
  /** null si aucun comptage physique de caisse saisi sur la période — distinct d'un écart de 0. */
  cashCollected: number | null
  cashGap: number | null
  dailyRevenueSeries: number[]
  /** Une entrée par jour, zéro explicite (pas de trou d'index comme dailyRevenueSeries) — ajouté
   *  pour l'écran Statistiques & Marges (étude study/web/statistique_web_page_redesign). */
  dailySeries: DailyBreakdownResponse[]
  /** Classement par CA sur toute la période (pas seulement aujourd'hui, contrairement à
   *  DashboardResponse.topProducts) avec marge réelle par produit. */
  topProfitableProducts: TopProfitableProductResponse[]
}

/** Client typé du module reporting — cf. boutiklic-core ReportingController (écrans E1, E2). */
export const reportingApi = {
  getDashboard: () => apiClient.get<DashboardResponse>('/reports/dashboard'),

  getBilan: (period: BilanPeriod = 'month') => apiClient.get<BilanResponse>(`/reports/bilan?period=${period}`),

  getBilanRange: (from: string, to: string) => apiClient.get<BilanResponse>(`/reports/bilan?from=${from}&to=${to}`),

  /** Renvoie un blob (PDF/image/Excel) — bypass apiClient (réponse binaire, pas JSON). */
  async export(period: BilanPeriod, format: ExportFormat): Promise<Blob> {
    return fetchExportBlob(`period=${period}&format=${format}`)
  },

  async exportRange(from: string, to: string, format: ExportFormat): Promise<Blob> {
    return fetchExportBlob(`from=${from}&to=${to}&format=${format}`)
  },

  /** Export brut ventes/stock/dettes/fournisseurs en ZIP de CSV (écran F3, "Exporter mes données"). */
  async dataExport(): Promise<Blob> {
    const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api'
    const response = await fetch(`${apiBase}/reports/data-export`, {
      headers: {
        Authorization: `Bearer ${getAccessToken() ?? ''}`,
        'X-Tenant-ID': getActiveTenantId() ?? '',
      },
    })
    if (!response.ok) throw new Error(`Export échoué (${response.status})`)
    return response.blob()
  },
}

async function fetchExportBlob(query: string): Promise<Blob> {
  const apiBase = import.meta.env.VITE_API_BASE_URL ?? '/api'
  const response = await fetch(`${apiBase}/reports/export?${query}`, {
    headers: {
      Authorization: `Bearer ${getAccessToken() ?? ''}`,
      'X-Tenant-ID': getActiveTenantId() ?? '',
    },
  })
  if (!response.ok) throw new Error(`Export échoué (${response.status})`)
  return response.blob()
}
